import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  ArrowLeft, CalendarDays, Clock, AlertCircle, Save, Search,
  CheckCircle, XCircle, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem } from '../../components/ui/select';

interface Student {
  id: number;
  student_name: string;
  roll_no: string;
  class_name: string;
  section_name: string;
}

interface AttendanceRecord {
  studentId: number;
  status: 'present' | 'absent' | 'late' | 'leave';
}

const statusConfig = {
  present: { color: 'bg-lime-500', icon: CheckCircle, label: 'Present', hex: '#22c55e' },
  absent: { color: 'bg-rose-500', icon: XCircle, label: 'Absent', hex: '#f43f5e' },
  late: { color: 'bg-amber-500', icon: Clock, label: 'Late', hex: '#f59e0b' },
  leave: { color: 'bg-sky-500', icon: AlertCircle, label: 'Leave', hex: '#0ea5e9' },
} as const;

type StatusKey = keyof typeof statusConfig;
const statusOrder: StatusKey[] = ['present', 'absent', 'late', 'leave'];

export default function AttendancePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClassId = searchParams.get('classId') || '';
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedClass, setSelectedClass] = useState(queryClassId);
  const [selectedBatch, setSelectedBatch] = useState('');
  const [search, setSearch] = useState('');
  const [attendance, setAttendance] = useState<Record<number, StatusKey>>({});
  const queryClient = useQueryClient();

  const { data: classesData } = useQuery({
    queryKey: ['classes-list'],
    queryFn: async () => api.get<any>('/api/classes'),
  });

  const { data: batchesData } = useQuery({
    queryKey: ['batches-list', selectedClass],
    queryFn: async () => api.get<any>(`/api/batches?classId=${selectedClass}`),
    enabled: !!selectedClass,
  });

  useEffect(() => {
    setAttendance({});
  }, [selectedDate, selectedClass, selectedBatch]);

  const { data: studentsData, isLoading: studentsLoading } = useQuery({
    queryKey: ['students-for-attendance', selectedClass, selectedBatch],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedClass) params.append('classId', selectedClass);
      if (selectedBatch) params.append('batchId', selectedBatch);
      params.append('status', 'active');
      params.append('pageSize', '500');
      return api.get<any>(`/api/students?${params.toString()}`);
    },
    enabled: !!selectedClass,
  });

  const { data: existingAttendance } = useQuery({
    queryKey: ['attendance', selectedDate, selectedClass, selectedBatch],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('date', selectedDate);
      if (selectedClass) params.append('classId', selectedClass);
      if (selectedBatch) params.append('batchId', selectedBatch);
      return api.get<any>(`/api/attendance?${params.toString()}`);
    },
    enabled: !!selectedClass && !!selectedDate,
  });

  useEffect(() => {
    if (existingAttendance?.data) {
      const records = existingAttendance.data as any[];
      const existing: Record<number, StatusKey> = {};
      records.forEach((record: any) => {
        const s = record.status;
        if (s in statusConfig) existing[record.student_id || record.studentId] = s;
      });
      setAttendance(existing);
    }
  }, [existingAttendance]);

  const markMutation = useMutation({
    mutationFn: async (records: AttendanceRecord[]) => {
      return api.post('/api/attendance/mark', {
        date: selectedDate,
        classId: parseInt(selectedClass),
        batchId: selectedBatch ? parseInt(selectedBatch) : undefined,
        records,
      });
    },
    onSuccess: (_, variables) => {
      const updated: Record<number, StatusKey> = { ...attendance };
      variables.forEach(r => { updated[r.studentId] = r.status; });
      setAttendance(updated);
      toast.success(`Attendance saved (${variables.length} students)`);
      queryClient.invalidateQueries({ queryKey: ['attendance', selectedDate, selectedClass, selectedBatch] });
      queryClient.invalidateQueries({ queryKey: ['student-attendance'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save attendance');
    },
  });

  const students: Student[] = (studentsData?.data?.items || studentsData?.data || []) as Student[];

  const filteredStudents = students.filter((s) =>
    s.student_name.toLowerCase().includes(search.toLowerCase()) ||
    (s.roll_no && s.roll_no.toLowerCase().includes(search.toLowerCase()))
  );

  const markAll = (status: StatusKey) => {
    const newAttendance: Record<number, StatusKey> = {};
    filteredStudents.forEach((s) => { newAttendance[s.id] = status; });
    setAttendance(prev => ({ ...prev, ...newAttendance }));
  };

  const toggleStatus = (studentId: number) => {
    const current = attendance[studentId];
    const idx = current ? statusOrder.indexOf(current) : -1;
    const next = statusOrder[(idx + 1) % statusOrder.length];
    setAttendance(prev => ({ ...prev, [studentId]: next }));
  };

  const handleSave = () => {
    const records: AttendanceRecord[] = Object.entries(attendance).map(([studentId, status]) => ({
      studentId: parseInt(studentId),
      status,
    }));
    if (records.length === 0) {
      toast.error('Mark at least one student');
      return;
    }
    markMutation.mutate(records);
  };

  const summary = useMemo(() => {
    const counts: Record<StatusKey, number> = { present: 0, absent: 0, late: 0, leave: 0 };
    Object.values(attendance).forEach(s => { if (s in counts) counts[s]++; });
    return counts;
  }, [attendance]);

  const totalMarked = Object.keys(attendance).length;
  const totalStudents = filteredStudents.length;
  const pct = totalStudents > 0 ? Math.round((totalMarked / totalStudents) * 100) : 0;

  return (
    <div className="space-y-4 pb-4">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          {queryClassId && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/modules/attendance/classes/${queryClassId}`)}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <h1 className="text-xl sm:text-2xl font-bold text-light-text dark:text-dark-text flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-lime-600" />
            Mark Attendance
          </h1>
        </div>
        <p className="text-xs text-light-text-muted mt-0.5">{new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <div>
              <label className="text-[10px] text-light-text-muted block mb-0.5">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="h-9 min-h-[36px] w-full rounded-xl border border-light-border dark:border-dark-border bg-light-elevated dark:bg-dark-elevated px-3 text-xs text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-purple-500/50 dark:[color-scheme:dark]"
              />
            </div>
            <div>
              <label className="text-[10px] text-light-text-muted block mb-0.5">Class</label>
              <Select value={selectedClass} onValueChange={(v) => { setSelectedClass(v); setSelectedBatch(''); }}>
                <SelectTrigger className="h-9 min-h-[36px] text-xs">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {(Array.isArray(classesData?.data) ? classesData.data : []).map((c: any) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-light-text-muted block mb-0.5">Batch</label>
              <Select value={selectedBatch || 'all'} onValueChange={(v) => setSelectedBatch(v === 'all' ? '' : v)} disabled={!selectedClass}>
                <SelectTrigger className="h-9 min-h-[36px] text-xs">
                  <SelectValue placeholder="All batches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Batches</SelectItem>
                  {(Array.isArray(batchesData?.data) ? batchesData.data : []).map((b: any) => (
                    <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] text-light-text-muted block mb-0.5">Search</label>
              <div className="relative">
                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-light-text-muted" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name..."
                  className="h-9 w-full min-h-[36px] rounded-xl border border-light-border dark:border-dark-border bg-light-elevated dark:bg-dark-elevated pl-6 pr-2 text-xs text-light-text dark:text-dark-text placeholder:text-light-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/50"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* No class selected */}
      {!selectedClass && (
        <div className="text-center py-16">
          <CalendarDays className="w-12 h-12 text-light-text-muted mx-auto mb-3" />
          <p className="text-sm text-light-text-muted">Select a class to start marking attendance</p>
        </div>
      )}

      {selectedClass && (
        <>
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex flex-wrap gap-1.5">
                  {statusOrder.map(status => {
                    const cfg = statusConfig[status];
                    const Icon = cfg.icon;
                    return (
                      <button
                        key={status}
                        onClick={() => markAll(status)}
                        className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-white text-[10px] sm:text-xs font-medium ${cfg.color} hover:opacity-90 transition-opacity`}
                      >
                        <Icon className="w-3 h-3" />
                        All {cfg.label}
                      </button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={markMutation.isPending || totalMarked === 0}
                  className="h-8 text-xs gap-1.5"
                >
                  {markMutation.isPending ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  Save ({totalMarked}/{totalStudents})
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-4 gap-2">
            {statusOrder.map(status => {
              const cfg = statusConfig[status];
              const Icon = cfg.icon;
              return (
                <div
                  key={status}
                  className="rounded-xl border border-light-border dark:border-dark-border bg-white dark:bg-dark-surface p-2.5 text-center shadow-sm"
                >
                  <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: cfg.hex }} />
                  <p className="font-black text-sm sm:text-base text-light-text dark:text-dark-text">{summary[status]}</p>
                  <p className="text-[9px] sm:text-[10px] text-light-text-muted">{cfg.label}</p>
                </div>
              );
            })}
          </div>

          {totalStudents > 0 && (
            <Card>
              <CardContent className="p-3">
                <div className="flex items-center justify-between text-[10px] mb-1.5">
                  <span className="text-light-text-muted">Progress</span>
                  <span className="font-semibold text-light-text dark:text-dark-text">{totalMarked}/{totalStudents} ({pct}%)</span>
                </div>
                <div className="w-full h-2 bg-light-elevated dark:bg-dark-elevated rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${pct}%`, background: pct === 100 ? '#22c55e' : pct > 50 ? '#a855f7' : '#f97316' }}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="overflow-hidden">
            {studentsLoading ? (
              <CardContent className="p-4 space-y-2">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="h-10 rounded-lg bg-light-elevated dark:bg-dark-elevated animate-pulse" />
                ))}
              </CardContent>
            ) : filteredStudents.length === 0 ? (
              <CardContent className="py-10 text-center text-light-text-muted text-sm">
                {search ? 'No students match your search' : 'No active students in this class'}
              </CardContent>
            ) : (
              <div className="divide-y divide-light-border dark:divide-dark-border">
                {filteredStudents.map((student, index) => {
                  const currentStatus = attendance[student.id];
                  const cfg = currentStatus ? statusConfig[currentStatus] : null;

                  return (
                    <div
                      key={student.id}
                      onClick={() => toggleStatus(student.id)}
                      className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors active:scale-[0.99] ${
                        cfg ? 'hover:bg-light-elevated/60 dark:hover:bg-dark-elevated/60' : 'hover:bg-light-elevated/40 dark:hover:bg-dark-elevated/40'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-transform duration-150 ${
                        cfg ? cfg.color : 'bg-gray-300 dark:bg-gray-600'
                      } text-white`}>
                        {cfg ? (
                          cfg.icon && <cfg.icon className="w-3.5 h-3.5" />
                        ) : (
                          <UserCheck className="w-3.5 h-3.5 opacity-50" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-light-text dark:text-dark-text whitespace-nowrap overflow-hidden text-ellipsis">
                          {student.student_name}
                        </p>
                        <p className="text-[10px] text-light-text-muted">
                          Roll {student.roll_no || '—'}
                          {student.section_name ? ` · ${student.section_name}` : ''}
                        </p>
                      </div>

                      <div className="shrink-0">
                        {cfg ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-light-elevated dark:bg-dark-elevated text-light-text dark:text-dark-text font-medium">
                            <span className={`w-1.5 h-1.5 rounded-full ${cfg.color}`} />
                            {cfg.label}
                          </span>
                        ) : (
                          <span className="text-[10px] text-light-text-muted px-2 py-1">Tap to mark</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
